import React from 'react';
import {string, bool, func} from 'prop-types';
import {Message, Button, Loader} from 'semantic-ui-react';
import BasicModal from 'components/common/BasicModal';
import {isNoU, makeDBOriginalImageUrl} from 'utils/DBUtils';
import ImageCropStep from './ImageCropStep.jsx';

const GIF_CONTENT_TYPE = 'image/gif';

// Modal step, mirroring ImageUploadModal's CropMode but with no Preview/tab-picker step --
// this modal is always scoped to one already-known image (the field's originalImageId),
// so it goes straight to fetching, then straight to Cropping once the bytes are in hand.
const Step = {
  Fetching: 'fetching',   // fetchRemoteImage in flight
  Failed: 'failed',       // fetchRemoteImage rejected
  Cropping: 'cropping',   // ImageCropStep is showing the interactive cropper (or, for a
                           // GIF source, its "uploaded as-is" preview)
  Reviewing: 'reviewing'  // showing the result of a completed crop -- Save sends it
};

/*
    Native (non-Filestack) re-crop path for the picker's "Edit Image" affordance (§10).
    Unlike ImageUploadModal (§3/§7), there is no source to pick -- this modal is always
    scoped to one already-uploaded image's `originalImageId`, the same "open a modal
    already scoped to one image" pattern ImageEditModal/ImageEditModalContainer use for
    Edit Info, not the "pick a new source" tab-picker pattern ImageUploadModal uses. So
    this is a small sibling component, not a third mode bolted onto ImageUploadModal.

    The original's bytes live in S3 (utils/DBUtils.makeDBOriginalImageUrl), a different
    origin from this app, so loading them into a <canvas> for cropping would taint it
    exactly like a cross-origin Link-tab source would -- see the approach doc's "The crop
    canvas only ever sees same-origin bytes" paragraph, and §7's own comment on
    ImageCropStep.jsx. This reuses the exact same fix: fetch the bytes through admin-api's
    GET /image/fetch (§6's RemoteImageFetcher, injected here as `fetchRemoteImage`, the
    same prop/helper §7 built for ImageUploadModalContainer), wrap the Blob in a same-origin
    File, and hand that to ImageCropStep exactly like a device upload.

    Presentational only -- dispatches nothing and calls no API of its own beyond the
    injected `fetchRemoteImage`. ImageRecropModalContainer supplies `onConfirm`, which
    dispatches the same nativeUploadImageAction §4's upload flow already dispatches (with
    preCropped: true and no mediaUrl) -- the write side is identical to a fresh native
    upload once bytes are in hand, so no new Redux tail was needed.
*/
export default class ImageRecropModal extends React.Component {
  constructor(props) {
    super(props);
    this.cropStepRef = React.createRef();
    // Bumped on every (re)open so a late fetchRemoteImage response from a previous
    // open -- e.g. a different field's image, or the same field clicked again quickly --
    // can be told apart from the one still wanted. Mirrors ImageUploadModal's
    // remoteFetchRequestId guard.
    this.requestId = 0;
    this.isUnmounted = false;
    this.state = this.getInitialState();
  }

  getInitialState = () => ({
    step: Step.Fetching,
    file: null,
    error: null,
    retryable: false,
    croppedResult: null,
    isSubmitting: false
  });

  componentDidMount() {
    if (this.props.isOpen) {
      this.startFetch();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.getInitialState(), this.startFetch);
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.revokeObjectUrls();
  }

  revokeObjectUrls = () => {
    if (this.state.croppedResult) {
      URL.revokeObjectURL(this.state.croppedResult.previewUrl);
    }
  };

  startFetch = () => {
    const requestId = (this.requestId += 1);
    const url = makeDBOriginalImageUrl(this.props.originalImageId);

    this.props.fetchRemoteImage(url).then(
      (blob) => {
        if (this.isUnmounted || !this.props.isOpen || requestId !== this.requestId) {
          return;
        }
        this.setState({
          step: Step.Cropping,
          // Filename is only ever used as the `name` handed to onConfirm (and from there,
          // ImageUploadServiceImpl's `<name>-<imageType>` convention for the new Image's
          // own name) -- the original's real filename isn't available client-side, only
          // its id, so a descriptive placeholder is used instead of a guess.
          file: new File([blob], `${this.props.imageType}-recrop`, {type: blob.type}),
          error: null
        });
      },
      (err) => {
        if (this.isUnmounted || !this.props.isOpen || requestId !== this.requestId) {
          return;
        }
        this.setState({
          step: Step.Failed,
          error: (err && err.message) || 'Couldn\'t load this image for cropping.',
          retryable: !isNoU(err) && (err.status === 0 || err.status >= 500)
        });
      }
    );
  };

  handleCropError = (message) => {
    this.setState({error: message});
  };

  // Applies the crop (or, for a GIF source, just pulls out the unmodified file --
  // ImageCropStep.getCroppedFile() already resolves that way) and shows the result.
  handleApplyCrop = () => {
    if (isNoU(this.cropStepRef.current)) {
      this.setState({error: 'The crop editor is not ready yet.'});
      return;
    }

    this.setState({isSubmitting: true});
    this.cropStepRef.current.getCroppedFile()
      .then((result) => {
        this.setState({
          isSubmitting: false,
          step: Step.Reviewing,
          croppedResult: {...result, previewUrl: URL.createObjectURL(result.file)}
        });
      })
      .catch((err) => {
        this.setState({
          isSubmitting: false,
          error: (err && err.message) || 'Failed to process the image.'
        });
      });
  };

  handleRecrop = () => {
    if (this.state.croppedResult) {
      URL.revokeObjectURL(this.state.croppedResult.previewUrl);
    }
    this.setState({croppedResult: null, step: Step.Cropping, error: null});
  };

  handleSave = () => {
    const {croppedResult} = this.state;
    if (isNoU(croppedResult)) {
      return;
    }
    this.props.onConfirm({
      file: croppedResult.file,
      name: croppedResult.name,
      contentType: croppedResult.contentType,
      preCropped: true
    });
  };

  handleCancel = () => {
    this.props.onCancel();
  };

  render() {
    const {step, file, error, retryable, croppedResult, isSubmitting} = this.state;
    const isFetching = step === Step.Fetching;
    const isFailed = step === Step.Failed;
    const isCropping = step === Step.Cropping;
    const isReviewing = step === Step.Reviewing;
    const isGif = !isNoU(file) && file.type === GIF_CONTENT_TYPE;

    const actions = [
      {
        label: 'Cancel',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleCancel
      }
    ];

    if (isReviewing) {
      actions.push({
        label: 'Recrop',
        isDisabled: isSubmitting,
        displayType: 'menu-item',
        action: this.handleRecrop
      });
    }

    let primaryLabel = isGif ? 'Continue' : 'Apply Crop';
    let primaryAction = this.handleApplyCrop;
    let primaryDisabled = !isCropping || isSubmitting || !isNoU(error);
    if (isReviewing) {
      primaryLabel = 'Save';
      primaryAction = this.handleSave;
      primaryDisabled = isSubmitting;
    } else if (isFetching || isFailed) {
      primaryDisabled = true;
    }

    actions.push({
      label: primaryLabel,
      isDisabled: primaryDisabled,
      loading: isSubmitting,
      displayType: 'button',
      primaryCheck: true,
      action: primaryAction
    });

    return (
      <BasicModal
        open={this.props.isOpen}
        header="Recrop Image"
        keepModalOnTop={true}
        specialType="primary-modal"
        onClose={this.handleCancel}
        actions={actions}>
        {isFetching && (
          <div className="db-image-crop-step">
            <Loader active inline="centered">Loading image for cropping…</Loader>
          </div>
        )}

        {isFailed && (
          <Message negative>
            <p>{error}</p>
            {retryable && (
              <Button type="button" basic onClick={this.startFetch}>Try again</Button>
            )}
          </Message>
        )}

        {isCropping && !isNoU(file) && (
          <div>
            {error && <Message negative>{error}</Message>}
            <ImageCropStep
              ref={this.cropStepRef}
              file={file}
              imageType={this.props.imageType}
              onError={this.handleCropError}
              onReplace={this.handleCancel}
              replaceLabel="Cancel" />
          </div>
        )}

        {isReviewing && !isNoU(croppedResult) && (
          <div className="db-image-crop-review">
            <img src={croppedResult.previewUrl} alt={croppedResult.name} style={{maxWidth: '100%'}} />
          </div>
        )}
      </BasicModal>
    );
  }
}

ImageRecropModal.propTypes = {
  originalImageId: string,
  imageType: string,
  isOpen: bool.isRequired,
  onCancel: func.isRequired,
  onConfirm: func.isRequired,
  fetchRemoteImage: func.isRequired
};



// WEBPACK FOOTER //
// ./public/components/images/ImageRecropModal.jsx